import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
} from "../../lib/redux";
import { triplePSDatasource } from "../../datasource";
import { InvoiceSuggestion, ApproveSuggestionBody } from "../../models";
import { RootState } from "../../app/store";

export interface InvoiceSuggestionsState {
  getSuggestions: APIResult<InvoiceSuggestion[]>;
  suggestions: InvoiceSuggestion[];
  approve: APIResult<void>;
  reject: APIResult<void>;
}

const initialState: InvoiceSuggestionsState = {
  getSuggestions: emptyAPIResult(),
  suggestions: [],
  approve: emptyAPIResult(),
  reject: emptyAPIResult(),
};

export const getSuggestions = createAsyncThunk(
  "invoiceSuggestions/getSuggestions",
  async () => {
    return triplePSDatasource.getInvoiceSuggestions("pending");
  }
);

export const approveSuggestion = createAsyncThunk(
  "invoiceSuggestions/approveSuggestion",
  async (data: { id: string; body: ApproveSuggestionBody }) => {
    return triplePSDatasource.approveInvoiceSuggestion(data.id, data.body);
  }
);

export const rejectSuggestion = createAsyncThunk(
  "invoiceSuggestions/rejectSuggestion",
  async (id: string) => {
    return triplePSDatasource.rejectInvoiceSuggestion(id);
  }
);

export const invoiceSuggestionsSlice = createSlice({
  name: "invoiceSuggestions",
  initialState,
  reducers: {
    resetSuggestions(state) {
      state.getSuggestions = initialState.getSuggestions;
      state.suggestions = [];
    },
    resetApprove(state) {
      state.approve = initialState.approve;
    },
    resetReject(state) {
      state.reject = initialState.reject;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSuggestions.pending, (state) => {
        state.getSuggestions = loadingAPIResult();
      })
      .addCase(getSuggestions.fulfilled, (state, action) => {
        state.getSuggestions = successAPIResult(action.payload);
        state.suggestions = action.payload;
      })
      .addCase(getSuggestions.rejected, (state, action) => {
        state.getSuggestions = errorAPIResult(action.error);
      })
      .addCase(approveSuggestion.pending, (state) => {
        state.approve = loadingAPIResult();
      })
      .addCase(approveSuggestion.fulfilled, (state) => {
        state.approve = successAPIResult(undefined);
      })
      .addCase(approveSuggestion.rejected, (state, action) => {
        state.approve = errorAPIResult(action.error);
      })
      .addCase(rejectSuggestion.pending, (state) => {
        state.reject = loadingAPIResult();
      })
      .addCase(rejectSuggestion.fulfilled, (state) => {
        state.reject = successAPIResult(undefined);
      })
      .addCase(rejectSuggestion.rejected, (state, action) => {
        state.reject = errorAPIResult(action.error);
      });
  },
});

export const { resetSuggestions, resetApprove, resetReject } =
  invoiceSuggestionsSlice.actions;

export const selectPendingSuggestions = (state: RootState) =>
  state.invoiceSuggestions.suggestions;
export const selectGetSuggestions = (state: RootState) =>
  state.invoiceSuggestions.getSuggestions;
export const selectApproveResult = (state: RootState) =>
  state.invoiceSuggestions.approve;
export const selectRejectResult = (state: RootState) =>
  state.invoiceSuggestions.reject;

export default invoiceSuggestionsSlice.reducer;
